import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Box, 
  Typography, 
  LinearProgress, 
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip
} from "@mui/material";
import { 
  VideoFile, 
  CheckCircle, 
  Error as ErrorIcon,
  PlayArrow,
  CloudUpload,
  Psychology,
  MenuBook,
  ExpandLess,
  ExpandMore,
  QuestionMark,
  Refresh,
  Edit,
  Delete,
  Close,
  Save
} from "@mui/icons-material";
import { supabase } from "../../supabaseClient";
import { API_URL } from "../../globals";
import axios from "axios";

const ProcessingStep = ({ onComplete, onGoBack, uploadedVideos = [], studioId }) => {
  const navigate = useNavigate();
  const [processedVideos, setProcessedVideos] = useState([]);
  const [currentStep, setCurrentStep] = useState(0); // 0: uploading, 1: processing, 2: generating guides
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState("");
  const [allTestCases, setAllTestCases] = useState([]);
  const [testCasesWithGuides, setTestCasesWithGuides] = useState([]);
  const [expandedVideos, setExpandedVideos] = useState({});
  const [stepStatuses, setStepStatuses] = useState({
    0: 'pending', // uploading: pending, processing, completed, error
    1: 'pending', // video processing: pending, processing, completed, error
    2: 'pending'  // guide generation: pending, processing, completed, error
  });
  // Using a ref instead of state prevents duplicated API calls that can occur
  // when React StrictMode double-invokes effects in development.
  const hasStartedProcessingRef = useRef(false);
  const [showContinue, setShowContinue] = useState(false);
  const [guideProgress, setGuideProgress] = useState({ completed: 0, total: 0 });
  const [currentlyProcessingGuides, setCurrentlyProcessingGuides] = useState([]);
  const [uploadedVideoIds, setUploadedVideoIds] = useState([]);
  const [showNoTestCasesPage, setShowNoTestCasesPage] = useState(false);
  const [processingETA, setProcessingETA] = useState(null);
  const [processingStartTime, setProcessingStartTime] = useState(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  // Ref to hold the polling timer so we can clear it if the component unmounts
  const pollingTimerRef = useRef(null);
  
  // New state for guide management
  const [guideModal, setGuideModal] = useState({ open: false, testCase: null, editedGuide: '', isEditing: false });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, testCase: null });
  
  // Track which steps have already been animated to prevent re-animations
  const hasAnimatedSteps = useRef({
    0: false, // upload step
    1: false, // processing step
    2: false  // guide generation step
  });

  const steps = [
    { title: "Uploading Videos", icon: CloudUpload, description: "Uploading your videos to the server..." },
    { title: "Processing Videos", icon: Psychology, description: "Analyzing videos to extract test cases..." },
    { title: "Generating Test Guides", icon: MenuBook, description: "Creating detailed step-by-step guides..." }
  ];

  useEffect(() => {
    if (uploadedVideos.length > 0) {
      processVideos();
    } else {
      setIsProcessing(false);
      setError("No videos to process");
    }
  }, [uploadedVideos]);

  // Timer effect for updating ETA countdown
  useEffect(() => {
    let interval;
    if (processingStartTime && processingETA && currentStep === 1 && stepStatuses[1] === 'processing') {
      interval = setInterval(() => {
        // Force re-render to update countdown and progress bar every second
        setUpdateTrigger(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [processingStartTime, processingETA, currentStep, stepStatuses]);

  // Clean up any pending polling timer when the component unmounts
  useEffect(() => {
    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
      }
    };
  }, []);

  const processVideos = async () => {
    // If we've already kicked off processing during this mount, skip.
    if (hasStartedProcessingRef.current) return;
    hasStartedProcessingRef.current = true;
    
    try {
      setIsProcessing(true);
      setCurrentStep(0); // Start with uploading step
      setStepStatuses({ 0: 'processing', 1: 'pending', 2: 'pending' });
      
      // Check flow types
      const isDemo = localStorage.getItem('inDemo') === 'true';
      const isCreateTests = localStorage.getItem('inCreateTests') === 'true';
      const isOnboarding = localStorage.getItem('inOnboarding') === 'true';
      let authToken = null;
      
      // In demo, skip authentication entirely regardless of other flags
      // For any non-demo flow, require authentication
      if (!isDemo) {
        const session = await supabase.auth.getSession();
        if (!session.data.session) {
          throw new Error('User not authenticated');
        }
        authToken = session.data.session.access_token;
      }
      
      // Initialize processed videos state with stable keys
      const initialProcessedVideos = uploadedVideos.map((video, index) => ({
        ...video,
        stableKey: `video-${index}-${Date.now()}`, // Stable key to prevent re-render glitches
        uploadStatus: 'pending',
        uploadProgress: 0,
        processingStatus: 'pending',
        testCases: [],
        videoId: null,
        error: null
      }));
      setProcessedVideos(initialProcessedVideos);

      // Step 1: Upload videos
      await uploadVideos(authToken, isDemo, isOnboarding);

    } catch (error) {
      console.error('Error in video processing workflow:', error);
      setStepStatuses(prev => ({ 
        ...prev, 
        [currentStep]: 'error'
      }));
      setError('Failed to process videos. Please try again.');
      setIsProcessing(false);
    }
  };

  const uploadVideos = async (authToken, isDemo = false, isOnboarding = false) => {
    try {
      // Update all videos to show upload in progress
      setProcessedVideos(prev => 
        prev.map(v => ({ 
          ...v, 
          uploadStatus: 'uploading',
          uploadProgress: 0
        }))
      );

      // Create FormData with all videos
      const formData = new FormData();
      uploadedVideos.forEach(video => {
        formData.append('videos', video.file);
      });

      // Prepare headers - include Authorization if we have a token (onboarding, CreateTests, or non-demo flows)
      const headers = {
        'Content-Type': 'multipart/form-data',
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await axios.post(`${API_URL}/test/video/upload`, formData, {
        headers,
        onUploadProgress: (progressEvent) => {
          const uploadProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          
          // Update progress for all videos
          setProcessedVideos(prev => 
            prev.map(v => ({ 
              ...v, 
              uploadProgress
            }))
          );
        }
      });

      // Update videos with upload results
      const uploadedVideoData = response.data.videos || [];
      setUploadedVideoIds(uploadedVideoData.map(v => v.video_id));
      
      setProcessedVideos(prev => 
        prev.map((v, index) => ({
          ...v,
          uploadStatus: 'completed',
          uploadProgress: 100,
          videoId: uploadedVideoData[index]?.video_id || null,
          uploadedFilename: uploadedVideoData[index]?.filename || v.name
        }))
      );

      // Mark upload as completed and show for 1 second
      setStepStatuses(prev => ({ ...prev, 0: 'completed' }));
      
      // Wait 1 second to let user digest the completion
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Move to step 1: Processing
      setStepStatuses(prev => ({ ...prev, 1: 'processing' }));
      setCurrentStep(1);

      // Step 2: Process videos  
      await processUploadedVideos(uploadedVideoData.map(v => v.video_id), authToken, isDemo, isOnboarding);

    } catch (error) {
      console.error('Error uploading videos:', error);
      setStepStatuses(prev => ({ ...prev, 0: 'error' }));
      setProcessedVideos(prev => 
        prev.map(v => ({ 
          ...v, 
          uploadStatus: 'error',
          error: error.response?.data?.message || error.message || 'Upload failed'
        }))
      );
      setError('Failed to upload videos. Please try again.');
      setIsProcessing(false);
    }
  };

  const processUploadedVideos = async (videoIds, authToken, isDemo = false, isOnboarding = false) => {
    try {
      // Calculate ETA based on longest video duration (40 seconds per minute of video)
      const longestVideoDuration = Math.max(...uploadedVideos.map(video => video.duration || 60));
      const minutes = Math.ceil(longestVideoDuration / 60);
      const estimatedSeconds = minutes * 40;
      setProcessingETA(estimatedSeconds);
      setProcessingStartTime(Date.now());

      // Mark all videos as currently processing
      setProcessedVideos(prev => prev.map(v => ({ ...v, processingStatus: 'processing' })));

      // Prepare headers
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const requestBody = { video_ids: videoIds };
      if (studioId) requestBody.studio_id = studioId;

      // Start processing (non-blocking, returns 202)
      const startResp = await axios.post(`${API_URL}/test/video/process`, requestBody, {
        headers,
        validateStatus: () => true, // we'll handle status codes manually
      });

      if (startResp.status >= 400 && startResp.status !== 202) {
        throw new Error(startResp.data?.message || 'Failed to start processing');
      }

      // job_id might be returned by backend; fallback to video_ids polling otherwise
      const processingJobId = startResp.data?.job_id || null;

      // Helper to poll processing status
      const pollStatus = async (attempt = 0) => {
        try {
          const statusResp = await axios.get(`${API_URL}/test/video/status`, {
            headers,
            params: processingJobId ? { job_id: processingJobId } : { video_ids: videoIds.join(',') },
          });

          const { statuses = [] } = statusResp.data || {};

          // Categorize statuses
          const completedStatuses = statuses.filter(s => s.status === 'processed');
          const errorStatuses = statuses.filter(s => s.status === 'error' || s.status === 'failed');

          // Update UI for each video based on latest status
          setProcessedVideos(prev =>
            prev.map(v => {
              const match = statuses.find(s => s.video_id === v.videoId);
              if (!match) return v;
              if (match.status === 'processed') {
                return { ...v, processingStatus: 'completed', testCases: match.cases || [] };
              }
              if (match.status === 'error' || match.status === 'failed') {
                return { ...v, processingStatus: 'error', error: match.error || 'Processing failed' };
              }
              // processing or other statuses
              return { ...v, processingStatus: 'processing' };
            })
          );

          // Check if all videos reached a terminal state
          const terminalCount = completedStatuses.length + errorStatuses.length;
          if (terminalCount === videoIds.length) {
            // Combine test cases from completed videos
            const combinedTestCases = [];
            completedStatuses.forEach(result => {
              if (result.cases) {
                result.cases.forEach(testCaseData => {
                  combinedTestCases.push({
                    case: testCaseData.case,
                    page_name: testCaseData.page_name,
                    videoId: result.video_id,
                    videoName: result.filename,
                  });
                });
              }
            });

            setAllTestCases(combinedTestCases);

            const hasSuccessful = completedStatuses.length > 0;
            const hasFailed = errorStatuses.length > 0;

            if (!hasSuccessful) {
              setStepStatuses(prev => ({ ...prev, 1: 'error' }));
              setError('All videos failed to process. Please try again or upload different videos.');
              setIsProcessing(false);
              return;
            } else if (hasFailed) {
              setError(`${completedStatuses.length} videos processed successfully, ${errorStatuses.length} failed.`);
            }

            setStepStatuses(prev => ({ ...prev, 1: 'completed' }));

            // Short pause for UX
            await new Promise(res => setTimeout(res, 1000));

            if (combinedTestCases.length > 0) {
              await generateTestGuides(combinedTestCases, authToken, isDemo, isOnboarding);
            } else {
              setIsProcessing(false);
              setShowNoTestCasesPage(true);
            }

            return; // stop polling
          }

          // Continue polling every 3 seconds
          pollingTimerRef.current = setTimeout(() => pollStatus(attempt + 1), 3000);
        } catch (err) {
          console.error('Error polling video status:', err);
          if (attempt < 10) {
            pollingTimerRef.current = setTimeout(() => pollStatus(attempt + 1), 5000);
          } else {
            setStepStatuses(prev => ({ ...prev, 1: 'error' }));
            setError('Failed to retrieve processing status. Please try again.');
            setIsProcessing(false);
          }
        }
      };

      // Start the first poll immediately
      pollStatus();

    } catch (error) {
      console.error('Error processing videos:', error);
      setStepStatuses(prev => ({ ...prev, 1: 'error' }));
      setProcessedVideos(prev =>
        prev.map(v => ({ ...v, processingStatus: 'error', error: error.response?.data?.message || error.message || 'Processing failed' }))
      );
      setError('Failed to process videos. Please try again.');
      setIsProcessing(false);
    }
  };

  const generateTestGuides = async (testCases, authToken, isDemo = false, isOnboarding = false) => {
    try {
      // Move to step 2: Generate guides
      setCurrentStep(2);
      setStepStatuses(prev => ({ ...prev, 2: 'processing' }));
      setGuideProgress({ completed: 0, total: testCases.length });
      setCurrentlyProcessingGuides(testCases);

      const guidesPromises = testCases.map(async (testCaseData, index) => {
        try {
          // Prepare headers - include Authorization if we have a token (onboarding, CreateTests, or non-demo flows)
          const headers = {
            'Content-Type': 'application/json',
          };
          if (authToken) {
            headers['Authorization'] = `Bearer ${authToken}`;
          }

          const response = await axios.post(`${API_URL}/test/video/test_guide`, {
            video_id: testCaseData.videoId,
            case: testCaseData.case,
            page_name: testCaseData.page_name
          }, {
            headers
          });

          // Update progress
          setGuideProgress(prev => ({ ...prev, completed: prev.completed + 1 }));

          return {
            ...testCaseData,
            guide: response.data.guide,
            success: true
          };
        } catch (error) {
          console.error(`Error generating guide for test case: ${testCaseData.case}`, error);
          
          // Update progress even for errors
          setGuideProgress(prev => ({ ...prev, completed: prev.completed + 1 }));

          return {
            ...testCaseData,
            guide: null,
            error: error.response?.data?.message || error.message || 'Failed to generate guide',
            success: false
          };
        }
      });

      const guidesResults = await Promise.all(guidesPromises);
      setTestCasesWithGuides(guidesResults);

      // Check if all guides were generated successfully
      const hasGuideErrors = guidesResults.some(result => !result.success);
      const hasSuccessfulGuides = guidesResults.some(result => result.success);

      if (!hasSuccessfulGuides) {
        // All guide generation failed
        setStepStatuses(prev => ({ ...prev, 2: 'error' }));
        setError("Failed to generate test guides. Please try again.");
        setIsProcessing(false);
      } else if (hasGuideErrors) {
        // Some guides failed
        setStepStatuses(prev => ({ ...prev, 2: 'completed' }));
        setError("Some test guides failed to generate, but others were successful.");
      } else {
        // All guides generated successfully
        setStepStatuses(prev => ({ ...prev, 2: 'completed' }));
      }

      // Complete guide generation
      if (hasSuccessfulGuides) {
        setCurrentStep(2);
        setStepStatuses(prev => ({ ...prev, 2: 'completed' }));
        
        // Wait 1 second to let user digest the completion, then show results
        setTimeout(() => {
          setIsProcessing(false);
          setShowContinue(true);
        }, 3000);
      }

    } catch (error) {
      console.error('Error in guide generation:', error);
      setStepStatuses(prev => ({ ...prev, 2: 'error' }));
      setError('Failed to generate test guides. Please try again.');
      setIsProcessing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      if (minutes === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      }
      return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    
    if (minutes > 0) {
      if (remainingSeconds === 0) {
        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
    }
    
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  };

  const getRemainingETA = () => {
    if (!processingETA || !processingStartTime) return null;
    const elapsed = Math.floor((Date.now() - processingStartTime) / 1000);
    const remaining = Math.max(0, processingETA - elapsed);
    return remaining;
  };

  const getUploadStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <CircularProgress size={20} sx={{ color: "#4a90e2" }} />;
      case 'completed':
        return <CheckCircle sx={{ color: "#4caf50", fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: "#f44336", fontSize: 20 }} />;
      default:
        return <VideoFile sx={{ color: "#94a3b8", fontSize: 20 }} />;
    }
  };

  const getProcessingStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <CircularProgress size={16} sx={{ color: "#4a90e2" }} />;
      case 'completed':
        return <CheckCircle sx={{ color: "#4caf50", fontSize: 16 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: "#f44336", fontSize: 16 }} />;
      default:
        return <Psychology sx={{ color: "#94a3b8", fontSize: 16 }} />;
    }
  };

  const toggleVideoExpansion = (videoIndex) => {
    setExpandedVideos(prev => ({
      ...prev,
      [videoIndex]: !prev[videoIndex]
    }));
  };

  const handleContinue = async () => {
    const testCasesToStore = testCasesWithGuides.length > 0 ? testCasesWithGuides : allTestCases;
    
    // Check if we're in the CreateTests flow
    const isCreateTests = localStorage.getItem('inCreateTests') === 'true';
    
    if (isCreateTests) {
      // For CreateTests flow, actually create the Playwright project
      try {
        setIsProcessing(true);
        
        // Get auth token
        const { data: { session } } = await supabase.auth.getSession();
        const authToken = session.access_token;

        // Create playwright project with studio_id
        const response = await axios.post(
          `${API_URL}/test/playwright_project`,
          {
            test_cases: testCasesToStore.map(tc => ({
              case: tc.case,
              page_name: tc.page_name,
              guide: tc.guide || null
            })),
            studio_id: studioId
          },
          {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.status === 200) {
          // Clean up localStorage data after successful submission
          localStorage.removeItem('onboardingTestCases');
          localStorage.removeItem('inCreateTests');
          
          onComplete(testCasesToStore);
        } else {
          setError("Failed to create project. Please try again.");
        }
      } catch (err) {
        console.error('Error creating playwright project:', err);
        setError(err.response?.data?.message || "An error occurred creating the project. Please try again.");
      } finally {
        setIsProcessing(false);
      }
    } else {
      // For regular onboarding flow, just store test cases
      localStorage.setItem('onboardingTestCases', JSON.stringify(testCasesToStore));
      onComplete(testCasesToStore);
    }
  };

  const handleRetry = () => {
    // Reset all states and go back to upload
    setShowNoTestCasesPage(false);
    onGoBack();
  };

  // No test cases found page
  const NoTestCasesPage = () => (
    <Box 
      sx={{ 
        width: "100%",
        py: 6,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "600px",
          mx: "auto",
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            backgroundColor: "rgba(255, 193, 7, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 4,
            animation: "pulseGlow 2s ease-in-out infinite",
            "@keyframes pulseGlow": {
              "0%, 100%": { 
                transform: "scale(1)",
                boxShadow: "0 0 20px rgba(255, 193, 7, 0.3)"
              },
              "50%": { 
                transform: "scale(1.05)",
                boxShadow: "0 0 30px rgba(255, 193, 7, 0.5)"
              }
            }
          }}
        >
          <QuestionMark sx={{ color: "#ffc107", fontSize: 48 }} />
        </Box>
        
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            mb: 3,
            letterSpacing: "-0.02em",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          }}
        >
          🤔 No App Flows Detected
        </Typography>
        
        <Typography
          variant="h6"
          sx={{
            color: "#94a3b8",
            fontWeight: 400,
            mb: 4,
            lineHeight: 1.6,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          }}
        >
          We couldn't find any testable app flows in your video{uploadedVideos.length > 1 ? 's' : ''}. 
          This might happen if the video doesn't show clear user interactions or app navigation.
        </Typography>

        <Box
          sx={{
            backgroundColor: "rgba(255, 193, 7, 0.1)",
            border: "1px solid rgba(255, 193, 7, 0.3)",
            borderRadius: 3,
            p: 4,
            mb: 6,
            textAlign: "left",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#ffc107",
              fontWeight: 600,
              mb: 2,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            💡 Tips for better results:
          </Typography>
          
          <Box component="ul" sx={{ color: "#94a3b8", pl: 2, m: 0 }}>
            <Typography component="li" sx={{ mb: 1.5, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" }}>
              <strong>Show clear user interactions:</strong> Tap buttons, navigate between screens, fill forms
            </Typography>
            <Typography component="li" sx={{ mb: 1.5, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" }}>
              <strong>Record complete workflows:</strong> Start from a clear beginning and show the full user journey
            </Typography>
            <Typography component="li" sx={{ mb: 1.5, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" }}>
              <strong>Keep movements slow and deliberate:</strong> Avoid quick gestures that might be hard to detect
            </Typography>
            <Typography component="li" sx={{ mb: 0, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" }}>
              <strong>Focus on one feature at a time:</strong> Record separate videos for different app features
            </Typography>
          </Box>
        </Box>

                 <Box sx={{ display: "flex", gap: 3, justifyContent: "center", flexWrap: "wrap" }}>
           <Button
             variant="contained"
             size="large"
             onClick={onGoBack}
             startIcon={<CloudUpload />}
             sx={{
               backgroundColor: "#ffffff",
               color: "#1a1a1a",
               px: 4,
               py: 2,
               borderRadius: 3,
               fontWeight: 600,
               fontSize: "1.1rem",
               fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
               boxShadow: "0 4px 16px rgba(255, 255, 255, 0.3)",
               transition: "all 0.3s ease",
               "&:hover": {
                 backgroundColor: "#f5f5f5",
                 transform: "translateY(-2px)",
                 boxShadow: "0 6px 20px rgba(255, 255, 255, 0.4)",
               },
             }}
           >
             Back to Video Upload
           </Button>
           
           <Button
             variant="outlined"
             size="large"
             onClick={handleRetry}
             startIcon={<Refresh />}
             sx={{
               borderColor: "rgba(255, 255, 255, 0.3)",
               color: "#ffffff",
               px: 4,
               py: 2,
               borderRadius: 3,
               fontWeight: 600,
               fontSize: "1.1rem",
               fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
               transition: "all 0.3s ease",
               "&:hover": {
                 borderColor: "#ffffff",
                 backgroundColor: "rgba(255, 255, 255, 0.1)",
                 transform: "translateY(-2px)",
               },
             }}
           >
             Try Different Videos
           </Button>
         </Box>
      </Box>
    </Box>
  );

  const StepCard = ({ stepIndex, title, icon: Icon, description, stepStatus }) => {
    const isActive = stepStatus === 'processing';
    const isCompleted = stepStatus === 'completed';
    const isError = stepStatus === 'error';
    const isPast = isCompleted && currentStep > stepIndex;
    
    // Track if this step should animate (only on first appearance)
    const shouldAnimate = (isActive || isCompleted || isError) && !hasAnimatedSteps.current[stepIndex];
    if (shouldAnimate) {
      hasAnimatedSteps.current[stepIndex] = true;
    }
    
    const borderColor = isActive ? "#4a90e2" : isCompleted ? "#4caf50" : isError ? "#f44336" : "rgba(255, 255, 255, 0.15)";
    
    return (
      <Card
        sx={{
          backgroundColor: isPast ? "rgba(255, 255, 255, 0.01)" : "rgba(255, 255, 255, 0.03)",
          border: `1px solid ${borderColor}`,
          borderRadius: 2,
          mb: isPast ? 1 : 3,
          transform: isPast ? "translateZ(-20px) scale(0.95)" : "translateZ(0) scale(1)",
          filter: isPast ? "brightness(0.6)" : "brightness(1)",
          boxShadow: isActive
            ? "0 0 6px rgba(74, 144, 226, 0.4)"
            : isCompleted && !isPast
              ? "0 0 6px rgba(76, 175, 80, 0.3)"
              : isError
                ? "0 0 6px rgba(244, 67, 54, 0.3)"
                : isPast
                  ? "0 2px 8px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(0, 0, 0, 0.3)"
                  : "none",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          opacity: isPast ? 0.7 : 1,
          position: "relative",
          zIndex: isPast ? 1 : isActive ? 3 : 2,
        }}
      >
        <CardContent sx={{ p: isActive ? 6 : isPast ? 2 : 4, transition: "padding 0.4s ease" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: isPast ? 0 : 2 }}>
            <Box
              sx={{
                width: isPast ? 32 : 48,
                height: isPast ? 32 : 48,
                borderRadius: "50%",
                backgroundColor: isActive ? "#4a90e2" : isCompleted ? "#4caf50" : isError ? "#f44336" : "rgba(255, 255, 255, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: isPast ? 2 : 3,
                transition: "all 0.3s ease",
              }}
            >
              {isActive && stepIndex <= 2 ? (
                <CircularProgress 
                  size={24} 
                  sx={{ 
                    color: "white",
                    animation: "spin 1s linear infinite, glow 2s ease-in-out infinite alternate",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" }
                    },
                    "@keyframes glow": {
                      "0%": { filter: "drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))" },
                      "100%": { filter: "drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))" }
                    }
                  }} 
                />
              ) : isCompleted ? (
                <CheckCircle 
                  sx={{ 
                    color: "white", 
                    fontSize: 24,
                    animation: "checkScale 0.5s ease-out",
                    "@keyframes checkScale": {
                      "0%": { transform: "scale(0)" },
                      "50%": { transform: "scale(1.2)" },
                      "100%": { transform: "scale(1)" }
                    }
                  }} 
                />
              ) : isError ? (
                <ErrorIcon 
                  sx={{ 
                    color: "white", 
                    fontSize: 24,
                    animation: "errorShake 0.5s ease-out",
                    "@keyframes errorShake": {
                      "0%, 100%": { transform: "translateX(0)" },
                      "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-3px)" },
                      "20%, 40%, 60%, 80%": { transform: "translateX(3px)" }
                    }
                  }} 
                />
              ) : (
                <Icon 
                  sx={{ 
                    color: "white", 
                    fontSize: isPast ? 16 : 24,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.1)"
                    }
                  }} 
                />
              )}
            </Box>
            <Box>
              <Typography
                variant={isPast ? "subtitle2" : "h6"}
                sx={{
                  color: "#ffffff",
                  fontWeight: isPast ? 500 : 600,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                }}
              >
                {title}
              </Typography>
              {!isPast && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#94a3b8",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  }}
                >
                  {description}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Upload Step Content */}
          {stepIndex === 0 && isActive && !isPast && (
            <Box 
              sx={{ 
                mt: 3,
                animation: shouldAnimate ? "slideInUp 0.6s ease-out" : "none",
                "@keyframes slideInUp": {
                  "0%": { 
                    opacity: 0, 
                    transform: "translateY(30px)" 
                  },
                  "100%": { 
                    opacity: 1, 
                    transform: "translateY(0)" 
                  }
                }
              }}
            >
              {processedVideos.map((video, index) => (
                <Box
                  key={video.stableKey || video.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.02)",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    transition: "background-color 0.3s ease, transform 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.04)",
                      transform: "translateX(4px)",
                    }
                  }}
                >
                  <Box sx={{ mr: 2 }}>
                    {getUploadStatusIcon(video.uploadStatus)}
                  </Box>
                  
                  <Box sx={{ flexGrow: 1, textAlign: "left" }}>
                    <Typography
                      sx={{
                        color: "#ffffff",
                        fontWeight: 500,
                        mb: 0.5,
                        fontSize: "0.9rem",
                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                      }}
                    >
                      {video.name}
                    </Typography>
                    
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                      }}
                    >
                      {formatFileSize(video.size)}
                    </Typography>

                    {video.uploadStatus === 'uploading' && (
                      <LinearProgress
                        variant="determinate"
                        value={video.uploadProgress}
                        sx={{
                          mt: 1,
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          overflow: "hidden",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "#4a90e2",
                            animation: "progressGlow 2s ease-in-out infinite",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                              animation: "progressShine 1.5s infinite",
                            }
                          },
                          "@keyframes progressGlow": {
                            "0%, 100%": { 
                              boxShadow: "0 0 5px rgba(74, 144, 226, 0.3)" 
                            },
                            "50%": { 
                              boxShadow: "0 0 15px rgba(74, 144, 226, 0.6)" 
                            }
                          },
                          "@keyframes progressShine": {
                            "0%": { transform: "translateX(-100%)" },
                            "100%": { transform: "translateX(100%)" }
                          }
                        }}
                      />
                    )}

                    {video.error && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#f44336",
                          display: "block",
                          mt: 0.5,
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                        }}
                      >
                        {video.error}
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: "#94a3b8",
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                    }}
                  >
                    {video.uploadProgress}%
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Video Processing Step Content */}
          {stepIndex === 1 && (isActive || isCompleted || isError) && !isPast && (
            <Box 
              sx={{ 
                mt: 3,
                animation: shouldAnimate ? "slideInUp 0.6s ease-out 0.2s both" : "none",
                "@keyframes slideInUp": {
                  "0%": { 
                    opacity: 0, 
                    transform: "translateY(30px)" 
                  },
                  "100%": { 
                    opacity: 1, 
                    transform: "translateY(0)" 
                  }
                }
              }}
            >
              {isActive ? (
                <>
                                    {/* ETA Progress Bar */}
                  {processingETA && (
                    <Box
                      sx={{
                        backgroundColor: "rgba(74, 144, 226, 0.1)",
                        border: "1px solid rgba(74, 144, 226, 0.3)",
                        borderRadius: 2,
                        p: 3,
                        mb: 3,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Typography
                          sx={{
                            color: "#4a90e2",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                          }}
                        >
                          ⏱️ Processing Progress
                        </Typography>
                        <Typography
                          sx={{
                            color: "#ffffff",
                            fontWeight: 500,
                            fontSize: "0.9rem",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                            minWidth: "80px", // Prevent layout shifts during countdown
                            textAlign: "right",
                          }}
                        >
                          {(() => {
                            const remaining = getRemainingETA();
                            if (remaining === null) return "Processing...";
                            if (remaining <= 0) return "Almost done...";
                            return formatTime(remaining) + " left";
                          })()}
                        </Typography>
                      </Box>
                      
                      <LinearProgress
                        variant="determinate"
                        value={(() => {
                          if (!processingStartTime || !processingETA) return 0;
                          const elapsed = Math.floor((Date.now() - processingStartTime) / 1000);
                          
                          // Add small randomness to make progress feel more natural
                          const randomFactor = 0.95 + (Math.sin(elapsed / 3) * 0.05); // Oscillates between 0.9 and 1.0
                          const baseProgress = (elapsed / processingETA) * 100 * randomFactor;
                          
                          // Add some natural variance but keep it smooth
                          const variance = Math.sin(elapsed / 7) * 3; // ±3% variance
                          const progress = Math.min(92, Math.max(0, baseProgress + variance)); // Cap at 92% until completion
                          
                          return progress;
                        })()}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "#4a90e2",
                            borderRadius: 4,
                            background: "linear-gradient(90deg, #4a90e2, #64b5f6)",
                            animation: "progressPulse 2s ease-in-out infinite",
                            transition: "transform 0.5s ease-out", // Smooth transitions
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                              animation: "progressShine 2s infinite",
                            }
                          },
                          "@keyframes progressPulse": {
                            "0%, 100%": { 
                              boxShadow: "0 0 5px rgba(74, 144, 226, 0.3)" 
                            },
                            "50%": { 
                              boxShadow: "0 0 15px rgba(74, 144, 226, 0.6)" 
                            }
                          },
                          "@keyframes progressShine": {
                            "0%": { transform: "translateX(-100%)" },
                            "100%": { transform: "translateX(100%)" }
                          }
                        }}
                      />
                    </Box>
                  )}
                
                {/* Show individual video processing status when active */}
                {processedVideos.map((video, index) => (
                  <Box
                    key={video.stableKey || video.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.02)",
                      borderRadius: 2,
                      p: 2,
                      mb: 2,
                      transition: "background-color 0.3s ease, transform 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.04)",
                        transform: "translateX(4px)",
                      }
                    }}
                  >
                    <Box sx={{ mr: 2 }}>
                      {getProcessingStatusIcon(video.processingStatus)}
                    </Box>
                    
                    <Box sx={{ flexGrow: 1, textAlign: "left" }}>
                      <Typography
                        sx={{
                          color: "#ffffff",
                          fontWeight: 500,
                          mb: 0.5,
                          fontSize: "0.9rem",
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                        }}
                      >
                        {video.uploadedFilename || video.name}
                      </Typography>
                      
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#64748b",
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                        }}
                      >
                        {video.processingStatus === 'completed' && video.testCases.length > 0 
                          ? `${video.testCases.length} test cases extracted`
                          : video.processingStatus === 'processing' 
                          ? 'Analyzing video...'
                          : video.processingStatus === 'error'
                          ? 'Processing failed'
                          : 'Waiting to process'}
                      </Typography>

                      {video.error && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#f44336",
                            display: "block",
                            mt: 0.5,
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                          }}
                        >
                          {video.error}
                        </Typography>
                      )}
                    </Box>

                    {video.processingStatus === 'completed' && (
                      <Chip
                        label={`${video.testCases.length} cases`}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(76, 175, 80, 0.2)",
                          color: "#4caf50",
                          fontSize: "0.75rem",
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                        }}
                      />
                    )}
                  </Box>
                ))}
                </>
              ) : isCompleted ? (
                // Show completion summary
                <Box
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.02)",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                  }}
                >
                  <CheckCircle sx={{ color: "#4caf50", fontSize: 32, mb: 1 }} />
                  <Typography
                    sx={{
                      color: "#4caf50",
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                      mb: 1,
                    }}
                  >
                    Processing Complete!
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#94a3b8",
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                    }}
                  >
                    {allTestCases.length} test cases extracted from your videos
                  </Typography>
                </Box>
              ) : isError ? (
                // Show error state
                <Box
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.02)",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                  }}
                >
                  <ErrorIcon sx={{ color: "#f44336", fontSize: 32, mb: 1 }} />
                  <Typography
                    sx={{
                      color: "#f44336",
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                    }}
                  >
                    Processing Failed
                  </Typography>
                </Box>
              ) : null}
            </Box>
          )}



          {/* Guide Generation Step Content */}
          {stepIndex === 2 && isActive && !isPast && (
            <Box 
              sx={{ 
                mt: 4,
                animation: shouldAnimate ? "slideInUp 0.6s ease-out 0.2s both" : "none",
                "@keyframes slideInUp": {
                  "0%": { 
                    opacity: 0, 
                    transform: "translateY(30px)" 
                  },
                  "100%": { 
                    opacity: 1, 
                    transform: "translateY(0)" 
                  }
                }
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
                  <CircularProgress size={24} sx={{ color: "#4a90e2", mr: 2 }} />
                  <Typography
                    sx={{
                      color: "#4a90e2",
                      fontSize: "1.1rem",
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                    }}
                  >
                    Generating detailed test guides...
                  </Typography>
                </Box>

                {/* Progress indicator */}
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#ffffff",
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                      mb: 1,
                    }}
                  >
                    {guideProgress.completed} of {guideProgress.total} guides completed
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(guideProgress.completed / guideProgress.total) * 100}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#4a90e2",
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>

                {/* List of test cases being processed */}
                <Box sx={{ 
                  mt: 4, 
                  textAlign: "left", 
                  maxHeight: "40vh", 
                  overflowY: "auto",
                  pr: 1, // Add padding for scrollbar
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    borderRadius: "3px",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                    },
                  },
                }}>
                  {currentlyProcessingGuides.map((testCase, index) => (
                    <Box
                      key={`${testCase.videoId}-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                        borderRadius: 2,
                        p: 2,
                        mb: 1.5,
                        border: index < guideProgress.completed ? "1px solid rgba(76, 175, 80, 0.3)" : 
                               index === guideProgress.completed ? "1px solid rgba(74, 144, 226, 0.5)" : 
                               "1px solid rgba(255, 255, 255, 0.1)",
                      }}
                    >
                      <Box sx={{ mr: 2 }}>
                        {index < guideProgress.completed ? (
                          <CheckCircle sx={{ color: "#4caf50", fontSize: 18 }} />
                        ) : index === guideProgress.completed ? (
                          <CircularProgress size={16} sx={{ color: "#4a90e2" }} />
                        ) : (
                          <MenuBook sx={{ color: "#94a3b8", fontSize: 18 }} />
                        )}
                      </Box>
                      
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          sx={{
                            color: "#ffffff",
                            fontWeight: 500,
                            fontSize: "0.9rem",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                            mb: 0.25,
                          }}
                        >
                          {testCase.case}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748b",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                          }}
                        >
                          from {testCase.videoName}{testCase.page_name ? ` • ${testCase.page_name}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {/* Guide Generation Step Content - Completed */}
          {stepIndex === 2 && isCompleted && !isPast && (
            <Box 
              sx={{ 
                mt: 3,
                animation: shouldAnimate ? "slideInUp 0.6s ease-out 0.2s both" : "none",
                "@keyframes slideInUp": {
                  "0%": { 
                    opacity: 0, 
                    transform: "translateY(30px)" 
                  },
                  "100%": { 
                    opacity: 1, 
                    transform: "translateY(0)" 
                  }
                }
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                }}
              >
                <CheckCircle sx={{ color: "#4caf50", fontSize: 32, mb: 1 }} />
                <Typography
                  sx={{
                    color: "#4caf50",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                    mb: 1,
                  }}
                >
                  Guides Generated!
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  }}
                >
                  {testCasesWithGuides.filter(tc => tc.success).length} detailed test guides created
                </Typography>
              </Box>
            </Box>
          )}

          {/* Guide Generation Step Content - Error */}
          {stepIndex === 2 && isError && !isPast && (
            <Box 
              sx={{ 
                mt: 3,
                animation: shouldAnimate ? "slideInUp 0.6s ease-out 0.2s both" : "none",
                "@keyframes slideInUp": {
                  "0%": { 
                    opacity: 0, 
                    transform: "translateY(30px)" 
                  },
                  "100%": { 
                    opacity: 1, 
                    transform: "translateY(0)" 
                  }
                }
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                }}
              >
                <ErrorIcon sx={{ color: "#f44336", fontSize: 32, mb: 1 }} />
                <Typography
                  sx={{
                    color: "#f44336",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  }}
                >
                  Guide Generation Failed
                </Typography>
              </Box>
            </Box>
          )}


        </CardContent>
      </Card>
    );
  };

  const handleCardClick = (testCase) => {
    setGuideModal({ 
      open: true, 
      testCase, 
      editedGuide: testCase.guide || '', 
      isEditing: false 
    });
  };

  const handleEditMode = () => {
    setGuideModal(prev => ({ ...prev, isEditing: true }));
  };

  const handleViewMode = () => {
    setGuideModal(prev => ({ ...prev, isEditing: false }));
  };

  const handleDeleteFromModal = () => {
    setDeleteConfirmDialog({ open: true, testCase: guideModal.testCase });
  };

  const confirmDeleteTestCase = () => {
    const testCaseToDelete = deleteConfirmDialog.testCase;
    
    // Remove from both arrays
    setTestCasesWithGuides(prev => 
      prev.filter(tc => 
        !(tc.case === testCaseToDelete.case && 
          tc.videoId === testCaseToDelete.videoId)
      )
    );
    
    setAllTestCases(prev => 
      prev.filter(tc => 
        !(tc.case === testCaseToDelete.case && 
          tc.videoId === testCaseToDelete.videoId)
      )
    );

    // Close both dialogs
    setDeleteConfirmDialog({ open: false, testCase: null });
    setGuideModal({ open: false, testCase: null, editedGuide: '', isEditing: false });
  };

  const saveEditedGuide = () => {
    const { testCase, editedGuide } = guideModal;
    
    // Update the guide in testCasesWithGuides
    setTestCasesWithGuides(prev => 
      prev.map(tc => 
        (tc.case === testCase.case && tc.videoId === testCase.videoId)
          ? { ...tc, guide: editedGuide }
          : tc
      )
    );

    // Update the modal with the new guide and switch back to view mode
    setGuideModal(prev => ({ 
      ...prev, 
      testCase: { ...prev.testCase, guide: editedGuide }, 
      isEditing: false 
    }));
  };

  // Show the no test cases page if applicable
  if (showNoTestCasesPage) {
    return <NoTestCasesPage />;
  }

  return (
    <Box 
      sx={{ 
        width: "100%",
        py: 6,
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "800px",
          mx: "auto",
          px: 3,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            mb: 3,
            letterSpacing: "-0.02em",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            animation: "titleFadeIn 0.8s ease-out",
            "@keyframes titleFadeIn": {
              "0%": { 
                opacity: 0, 
                transform: "translateY(-20px)" 
              },
              "100%": { 
                opacity: 1, 
                transform: "translateY(0)" 
              }
            }
          }}
        >
          {isProcessing ? "Processing Your Videos" : error ? "Processing Failed" : "🎉 Processing Complete"}
        </Typography>
        
        <Typography
          variant="h6"
          sx={{
            color: "#94a3b8",
            fontWeight: 400,
            mb: 6,
            lineHeight: 1.6,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            animation: "subtitleFadeIn 0.8s ease-out 0.2s both",
            "@keyframes subtitleFadeIn": {
              "0%": { 
                opacity: 0, 
                transform: "translateY(20px)" 
              },
              "100%": { 
                opacity: 1, 
                transform: "translateY(0)" 
              }
            }
          }}
        >
          {isProcessing 
            ? "We're processing your videos step by step..."
            : testCasesWithGuides.length > 0 
              ? `Successfully created ${testCasesWithGuides.filter(tc => tc.success).length} detailed test guides`
              : allTestCases.length > 0 
                ? `Successfully extracted ${allTestCases.length} test cases from your videos`
                : "No test cases could be extracted"}
        </Typography>

        {error && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 4,
              backgroundColor: "rgba(255, 152, 0, 0.1)",
              color: "#ff9800",
              "& .MuiAlert-icon": {
                color: "#ff9800"
              }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Go Back Button - Show when there are no videos to process */}
        {uploadedVideos.length === 0 && !isProcessing && (
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={onGoBack}
              sx={{
                borderColor: "#ffffff",
                color: "#ffffff",
                px: 6,
                py: 2,
                borderRadius: 3,
                fontWeight: 600,
                fontSize: "1.1rem",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#f5f5f5",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                }
              }}
            >
              ← Go Back to Upload Videos
            </Button>
          </Box>
        )}

        {/* Processing Steps */}
        <Box 
          sx={{ 
            position: "relative",
            pt: 3,
            perspective: "1000px", // Add 3D perspective
          }}
        >
          {!showContinue ? (
            <Box
              sx={{
                textAlign: "left",
                px: 3,
              }}
            >
              {steps.map((step, index) => (
                <StepCard
                  key={index}
                  stepIndex={index}
                  title={step.title}
                  icon={step.icon}
                  description={step.description}
                  stepStatus={stepStatuses[index]}
                />
              ))}
            </Box>
          ) : (
            // Show test cases when processing is complete
            <Box
              sx={{
                animation: "fadeInScale 0.8s ease-out",
                "@keyframes fadeInScale": {
                  "0%": { 
                    opacity: 0, 
                    transform: "scale(0.9)" 
                  },
                  "100%": { 
                    opacity: 1, 
                    transform: "scale(1)" 
                  }
                }
              }}
            >
              {(() => {
                const isDemo = localStorage.getItem('inDemo') === 'true';
                const isCreateTests = localStorage.getItem('inCreateTests') === 'true';
                const isOnboarding = localStorage.getItem('inOnboarding') === 'true';
                const availableTestCases = testCasesWithGuides.length > 0 ? testCasesWithGuides.filter(tc => tc.success) : allTestCases;
                const demoLimit = 5;
                // Only apply demo limitations if it's actually a demo AND not onboarding/CreateTests flows
                const shouldApplyDemoLimit = isDemo && !isCreateTests && !isOnboarding;
                const visibleTestCases = shouldApplyDemoLimit ? availableTestCases.slice(0, demoLimit) : availableTestCases;
                const lockedTestCases = shouldApplyDemoLimit && availableTestCases.length > demoLimit ? availableTestCases.slice(demoLimit) : [];

                return (
                  <>
                    {/* Demo limitation banner */}
                    {shouldApplyDemoLimit && availableTestCases.length > demoLimit && (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mb: 3,
                          mx: 3,
                          backgroundColor: "rgba(74, 144, 226, 0.1)",
                          color: "#4a90e2",
                          border: "1px solid rgba(74, 144, 226, 0.3)",
                          "& .MuiAlert-icon": {
                            color: "#4a90e2"
                          }
                        }}
                      >
                        <Typography sx={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" }}>
                          <strong>Interactive Demo:</strong> Only 5 test cases are available in the demo. 
                          <Link 
                            component="button"
                            onClick={() => navigate('/signup')}
                            sx={{ 
                              color: "#4a90e2", 
                              fontWeight: 600,
                              textDecoration: "underline",
                              cursor: "pointer",
                              background: "none",
                              border: "none",
                              p: 0,
                              fontFamily: "inherit",
                              fontSize: "inherit",
                              "&:hover": {
                                color: "#64b5f6",
                                textDecoration: "underline"
                              }
                            }}
                          >
                            Sign up
                          </Link> to access all {availableTestCases.length} test cases and unlock the full platform.
                        </Typography>
                      </Alert>
                    )}

                    <Box sx={{ 
                      maxHeight: shouldApplyDemoLimit && lockedTestCases.length > 0 ? "60vh" : "50vh", 
                      overflowY: "auto", 
                      px: 3, 
                      textAlign: "left",
                      pr: 4, // Add padding for scrollbar
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        borderRadius: "3px",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.5)",
                        },
                      },
                    }}>
                      {/* Available test cases */}
                      {visibleTestCases.map((testCase, index) => (
                        <Box
                          key={`final-${testCase.videoId || index}-${index}`}
                          onClick={() => handleCardClick(testCase)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.03)",
                            border: "1px solid rgba(76, 175, 80, 0.3)",
                            borderRadius: 3,
                            p: 4,
                            mb: 3,
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            textAlign: "left",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.08)",
                              transform: "translateX(8px) scale(1.02)",
                              boxShadow: "0 8px 32px rgba(76, 175, 80, 0.3)",
                              borderColor: "rgba(76, 175, 80, 0.5)",
                            }
                          }}
                        >
                          <Box sx={{ mr: 3 }}>
                            <CheckCircle sx={{ color: "#4caf50", fontSize: 24 }} />
                          </Box>
                          
                          <Box sx={{ flexGrow: 1, textAlign: "left" }}>
                            <Typography
                              sx={{
                                color: "#ffffff",
                                fontWeight: 600,
                                fontSize: "1.1rem",
                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                                mb: 1,
                                textAlign: "left",
                              }}
                            >
                              {testCase.case || testCase.case}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#64748b",
                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                                textAlign: "left",
                              }}
                            >
                              from {testCase.videoName}{testCase.page_name ? ` • ${testCase.page_name}` : ''}
                              {testCase.guide && " • Complete guide generated"}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                              label={testCase.guide ? "Click to view/edit" : "Click to view"}
                              sx={{
                                backgroundColor: testCase.guide ? "rgba(76, 175, 80, 0.2)" : "rgba(74, 144, 226, 0.2)",
                                color: testCase.guide ? "#4caf50" : "#4a90e2",
                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                              }}
                            />
                          </Box>
                        </Box>
                      ))}

                      {/* Locked test cases for demo */}
                      {shouldApplyDemoLimit && lockedTestCases.map((testCase, index) => (
                        <Box
                          key={`locked-${testCase.videoId || index}-${index}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.01)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: 3,
                            p: 4,
                            mb: 3,
                            textAlign: "left",
                            position: "relative",
                            opacity: 0.4,
                            cursor: "not-allowed",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: "linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.05) 50%, transparent 70%)",
                              pointerEvents: "none",
                            },
                            "&::after": {
                              content: '"🔒"',
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              fontSize: "2rem",
                              zIndex: 2,
                              textShadow: "0 0 10px rgba(255, 255, 255, 0.5)",
                            }
                          }}
                        >
                          <Box sx={{ mr: 3 }}>
                            <CheckCircle sx={{ color: "#94a3b8", fontSize: 24 }} />
                          </Box>
                          
                          <Box sx={{ flexGrow: 1, textAlign: "left" }}>
                            <Typography
                              sx={{
                                color: "#94a3b8",
                                fontWeight: 600,
                                fontSize: "1.1rem",
                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                                mb: 1,
                                textAlign: "left",
                              }}
                            >
                              {testCase.case || testCase.case}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#64748b",
                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                                textAlign: "left",
                              }}
                            >
                              from {testCase.videoName}{testCase.page_name ? ` • ${testCase.page_name}` : ''}
                              {testCase.guide && " • Complete guide generated"}
                            </Typography>
                          </Box>

                          <Chip
                            label={"Locked"}
                            sx={{
                              backgroundColor: "rgba(148, 163, 184, 0.2)",
                              color: "#94a3b8",
                              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </>
                );
              })()}
            </Box>
          )}
        </Box>
            
        {/* Continue Button */}
        {showContinue && (
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              sx={{
                backgroundColor: "#ffffff",
                color: "#1a1a1a",
                px: 6,
                py: 2,
                borderRadius: 3,
                fontWeight: 600,
                fontSize: "1.2rem",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                boxShadow: "0 4px 16px rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(255, 255, 255, 0.4)",
                },
                "&:active": {
                  transform: "translateY(0)",
                }
              }}
            >
              {(() => {
                const isDemo = localStorage.getItem('inDemo') === 'true';
                const isCreateTests = localStorage.getItem('inCreateTests') === 'true';
                const isOnboarding = localStorage.getItem('inOnboarding') === 'true';
                const availableTestCases = testCasesWithGuides.length > 0 
                  ? testCasesWithGuides.filter(tc => tc.success) 
                  : allTestCases;
                const demoLimit = 5;
                const shouldApplyDemoLimit = isDemo && !isCreateTests && !isOnboarding;
                const testCaseCount = shouldApplyDemoLimit && availableTestCases.length > demoLimit 
                  ? demoLimit 
                  : availableTestCases.length;
                
                return `Start with ${testCaseCount} Test Case${testCaseCount !== 1 ? 's' : ''}${shouldApplyDemoLimit && availableTestCases.length > demoLimit ? ' (Demo)' : ''}`;
              })()}
            </Button>
          </Box>
        )}
      </Box>
      
      {/* Unified Guide Modal */}
      <Dialog
        open={guideModal.open}
        onClose={() => setGuideModal({ open: false, testCase: null, editedGuide: '', isEditing: false })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a1a",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: 3,
            color: "#ffffff",
          }
        }}
      >
        <DialogTitle sx={{ 
          color: "#ffffff", 
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {guideModal.isEditing ? "Edit Test Guide" : "Test Guide"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8", fontWeight: 400 }}>
              {guideModal.testCase?.case}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setGuideModal({ open: false, testCase: null, editedGuide: '', isEditing: false })}
            sx={{ color: "#94a3b8" }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {guideModal.isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={20}
              value={guideModal.editedGuide}
              onChange={(e) => setGuideModal(prev => ({ ...prev, editedGuide: e.target.value }))}
              placeholder="Enter the test guide instructions..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  color: "#ffffff",
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ffb74d",
                  },
                },
                "& .MuiInputBase-input": {
                  color: "#ffffff",
                },
                "& .MuiInputLabel-root": {
                  color: "#94a3b8",
                },
              }}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: 2,
                p: 3,
                maxHeight: "60vh",
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                  borderRadius: "3px",
                },
              }}
            >
              <Typography
                sx={{
                  color: "#ffffff",
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {guideModal.testCase?.guide || 'No guide available yet. Click "Edit Guide" to create one.'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid rgba(255, 255, 255, 0.1)", gap: 1 }}>
          <Button
            onClick={handleDeleteFromModal}
            startIcon={<Delete />}
            sx={{
              color: "#f48fb1",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              "&:hover": {
                backgroundColor: "rgba(244, 143, 177, 0.1)",
              },
            }}
          >
            Delete Test Case
          </Button>
          
          <Box sx={{ flexGrow: 1 }} />
          
          {guideModal.isEditing ? (
            <>
              <Button
                onClick={handleViewMode}
                sx={{
                  color: "#64b5f6",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={saveEditedGuide}
                variant="contained"
                startIcon={<Save />}
                sx={{
                  backgroundColor: "#ffffff",
                  color: "#000000",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  "&:hover": {
                    backgroundColor: "#45a049",
                  },
                }}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <Button
              onClick={handleEditMode}
              variant="contained"
              startIcon={<Edit />}
              sx={{
                backgroundColor: "#ffb74d",
                color: "#1a1a1a",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                "&:hover": {
                  backgroundColor: "#ffa726",
                },
              }}
            >
              Edit Guide
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, testCase: null })}
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a1a",
            border: "1px solid rgba(244, 67, 54, 0.3)",
            borderRadius: 3,
            color: "#ffffff",
          }
        }}
      >
        <DialogTitle sx={{ 
          color: "#f44336", 
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          display: "flex",
          alignItems: "center",
          gap: 2
        }}>
          <Delete />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Test Case
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography sx={{ 
            color: "#ffffff", 
            mb: 2,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          }}>
            Are you sure you want to delete this test case?
          </Typography>
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.02)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: 2,
              p: 3,
            }}
          >
            <Typography
              sx={{
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "1rem",
                mb: 1,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              }}
            >
              {deleteConfirmDialog.testCase?.case}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#94a3b8",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              }}
            >
              from {deleteConfirmDialog.testCase?.videoName}
              {deleteConfirmDialog.testCase?.page_name ? ` • ${deleteConfirmDialog.testCase.page_name}` : ''}
            </Typography>
          </Box>
          <Typography sx={{ 
            color: "#f44336", 
            mt: 2,
            fontSize: "0.9rem",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid rgba(255, 255, 255, 0.1)", gap: 1 }}>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, testCase: null })}
            sx={{
              color: "#94a3b8",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteTestCase}
            variant="contained"
            startIcon={<Delete />}
            sx={{
              backgroundColor: "#f44336",
              color: "#ffffff",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              "&:hover": {
                backgroundColor: "#d32f2f",
              },
            }}
          >
            Delete Test Case
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessingStep; 